import type { Integration } from "@sentry/core";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const getGlobalState = (): typeof globalThis & {
  __sentry_initialized__?: boolean;
} => globalThis as typeof globalThis & { __sentry_initialized__?: boolean };

const isProduction =
  import.meta.env.PROD || import.meta.env.VITE_DEPLOYMENT === "production";

const normalizeUrl = (url: string): string => url.replace(/\/$/, "");

const buildIntegrations = (): Integration[] => {
  const integrations = [
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ];

  return integrations;
};

export const initSentry = (): void => {
  const globalState = getGlobalState();

  if (globalState.__sentry_initialized__) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const backendUrl = import.meta.env.VITE_BACKENDURL;
  const tunnelUrl =
    import.meta.env.VITE_SENTRY_TUNNEL_URL ||
    (backendUrl ? `${normalizeUrl(backendUrl)}/sentry-tunnel` : undefined);

  if (!dsn) {
    return;
  }

  globalState.__sentry_initialized__ = true;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_DEPLOYMENT,
    ...(tunnelUrl && { tunnel: tunnelUrl }),
    sendDefaultPii: true,
    integrations: buildIntegrations(),
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    tracePropagationTargets: [
      "localhost",
      backendUrl,
      import.meta.env.VITE_FILES_BASEURL,
    ],
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
  });
};
